import React, { useState } from 'react';
import { Briefcase, TrendingUp, Users, Award, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { CAREERS_PAGE } from '../content';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Careers = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    cv: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast({
          title: "❌ Fichier trop volumineux",
          description: `Le fichier dépasse la limite de 5MB (taille: ${(file.size / (1024 * 1024)).toFixed(2)}MB). Veuillez choisir un fichier plus petit.`,
          variant: "destructive",
          duration: 6000,
        });
        e.target.value = ''; // Reset file input
        return;
      }
      setFormData(prev => ({ ...prev, cv: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let cvFileName = null;
      let cvOriginalName = null;
      
      // Upload CV file first if present
      if (formData.cv) {
        const formDataFile = new FormData();
        formDataFile.append('file', formData.cv);
        
        const uploadResponse = await fetch(`${BACKEND_URL}/api/upload/cv`, {
          method: 'POST',
          body: formDataFile
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload CV');
        }
        
        const uploadResult = await uploadResponse.json();
        cvFileName = uploadResult.filename;
        cvOriginalName = uploadResult.original_filename;
      }
      
      // Prepare application data
      const applicationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        cvFileName: cvFileName,
        cvOriginalName: cvOriginalName
      };

      const response = await fetch(`${BACKEND_URL}/api/careers/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      // Show success message
      toast({
        title: "✅ Candidature envoyée avec succès !",
        description: "Merci pour votre intérêt. Notre équipe RH examinera votre candidature et vous contactera dans les plus brefs délais.",
        duration: 5000,
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        cv: null
      });
      
      // Reset file input
      const fileInput = document.getElementById('cv');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "❌ Erreur lors de l'envoi",
        description: "Une erreur s'est produite lors de l'envoi de votre candidature. Veuillez réessayer ou nous contacter directement.",
        variant: "destructive",
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 via-white to-pink-50 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full mb-6">
              <span className="text-green-700 font-semibold text-sm">Nous recrutons</span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Rejoignez <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">l'aventure</span> RT Distribution
            </h1>
            <p className="text-xl text-gray-600">
              Participez à la croissance d'une entreprise dynamique et innovante dans le secteur de l'énergie
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{CAREERS_PAGE.whyJoin.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {CAREERS_PAGE.whyJoin.description}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {CAREERS_PAGE.whyJoin.reasons.map((reason, index) => {
              const icons = [TrendingUp, Award, Users, Briefcase];
              const IconComponent = icons[index];
              const gradients = [
                'from-green-500 to-emerald-600',
                'from-pink-500 to-pink-600',
                'from-emerald-500 to-green-600',
                'from-green-600 to-emerald-700'
              ];
              return (
                <div key={index} className="text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${gradients[index]} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                  <p className="text-gray-600">
                    {reason.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* Job Offers */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Postes à pourvoir</h2>
            <p className="text-xl text-gray-600">Découvrez nos opportunités actuelles</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {CAREERS_PAGE.jobs.offers.map((job) => (
              <div key={job.id} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h3>
                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                        {job.type}
                      </span>
                    </div>
                  </div>
                  <Briefcase className="text-green-600" size={24} />
                </div>
                <p className="text-gray-600 mb-4">{job.description}</p>
                <p className="text-sm text-gray-500 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {job.location}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Postuler maintenant</h2>
              <p className="text-xl text-gray-600">
                Remplissez ce formulaire rapide et nous reviendrons vers vous
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cv">CV (PDF, max 5MB)</Label>
                  <div className="relative">
                    <Input
                      id="cv"
                      name="cv"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="cursor-pointer"
                    />
                    <Upload className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
                  </div>
                  {formData.cv && (
                    <p className="text-sm text-green-600">✓ {formData.cv.name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Parlez-nous de vous et de vos motivations..."
                    rows={5}
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma candidature'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;